import React, { useEffect, useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, MenuItem, Switch, TextField, Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import SavingOverlay from "../SavingOverlay";
import {
  healthPlansService,
  HealthPlanOperatorOption,
  HealthPlanRow,
} from "../../services/healthPlansService";
import { colors, typography, forms } from "../../theme/designSystem";

/**
 * Cadastro de plano (RN-411b), extraído da tela de Planos em 19/09/2026 para ser usado também
 * dentro de Convênios, quando o plano virou uma funcionalidade do convênio (item 2 do pacote).
 * Um formulário só: duplicar aqui significaria duas regras de vigência para o mesmo dado.
 *
 * <p><b>operatorId fixo</b> quando o modal abre a partir da linha de um convênio — ali não há o
 * que escolher, e oferecer o combo convidaria a mover o plano sem querer.</p>
 */
interface Props {
  open: boolean;
  /** Plano a editar; nulo = novo. */
  plan: HealthPlanRow | null;
  operators: HealthPlanOperatorOption[];
  /** Quando vem da linha do convênio: o campo não aparece. */
  fixedOperatorId?: string;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

interface FormState {
  id: string | null;
  operatorId: string;
  name: string;
  ansProductCode: string;
  copayment: "" | "yes" | "no";
  startDate: string;
  endDate: string;
  active: boolean;
}

const digits = (v: string) => v.replace(/\D/g, "");

const dialogActionsSx = {
  padding: "1.5rem 2rem",
  borderTop: `1px solid ${colors.backgroundAlt}`,
  backgroundColor: colors.background,
  gap: "1rem",
};

const HealthPlanModal: React.FC<Props> = ({
  open, plan, operators, fixedOperatorId, onClose, onSaved, onError,
}) => {
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSaving(false);
    setForm(plan
      ? {
        id: plan.id,
        operatorId: plan.operatorId,
        name: plan.name,
        ansProductCode: plan.ansProductCode ?? "",
        copayment: plan.copayment === null ? "" : plan.copayment ? "yes" : "no",
        startDate: plan.startDate ?? "",
        endDate: plan.endDate ?? "",
        active: plan.active,
      }
      : {
        id: null,
        operatorId: fixedOperatorId ?? "",
        name: "", ansProductCode: "", copayment: "", startDate: "", endDate: "", active: true,
      });
  }, [open, plan, fixedOperatorId]);

  const activeOperators = operators.filter((o) => o.active);

  const save = async () => {
    if (!form || saving) return;
    if (!form.operatorId) { onError("Escolha o convênio."); return; }
    if (!form.name.trim()) { onError("Informe o nome do plano."); return; }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      onError("O fim da vigência não pode ser anterior ao início.");
      return;
    }
    if (form.ansProductCode && form.ansProductCode.length !== 9) {
      onError("O registro do plano na ANS tem 9 dígitos.");
      return;
    }
    setSaving(true);
    try {
      await healthPlansService.save({
        id: form.id,
        operatorId: form.operatorId,
        name: form.name.trim(),
        ansProductCode: form.ansProductCode || null,
        copayment: form.copayment === "" ? null : form.copayment === "yes",
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        active: form.active,
      });
      onSaved(form.id ? "Plano atualizado." : "Plano incluído.");
      onClose();
    } catch (e: unknown) {
      const message = (e as { response?: { data?: { description?: string } } })
        ?.response?.data?.description;
      onError(message || "Erro ao salvar o plano.");
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
    >
      <SavingOverlay open={saving} message="Salvando..." />
      <DialogTitle sx={{
        backgroundColor: colors.primary, color: colors.white, display: "flex",
        justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem",
      }}>
        <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
          {form?.id ? "Editar plano" : "Novo plano"}
        </Typography>
        <IconButton onClick={onClose} disabled={saving} sx={{ color: colors.white, padding: "0.25rem" }} aria-label="Fechar">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
        {form && (
          <Box sx={{
            display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            columnGap: forms.fieldGapX, rowGap: forms.fieldGapY, alignItems: "center",
          }}>
            {!fixedOperatorId && (
              <TextField
                select
                label="Convênio"
                value={form.operatorId}
                onChange={(e) => setForm({ ...form, operatorId: e.target.value })}
                size="small"
                fullWidth
                required
                disabled={form.id !== null}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                sx={{ gridColumn: { sm: "1 / -1" } }}
              >
                <MenuItem value="" disabled>Selecione</MenuItem>
                {(form.id ? operators : activeOperators).map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Nome do plano"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              size="small"
              fullWidth
              required
              placeholder="Ex.: Nacional Apartamento"
              inputProps={{ maxLength: 150 }}
              InputLabelProps={{ shrink: true }}
              sx={{ gridColumn: { sm: "1 / -1" } }}
            />
            <TextField
              label="Registro do plano na ANS"
              value={form.ansProductCode}
              onChange={(e) => setForm({ ...form, ansProductCode: digits(e.target.value).slice(0, 9) })}
              size="small"
              fullWidth
              placeholder="9 dígitos (opcional)"
              inputProps={{ maxLength: 9, inputMode: "numeric" }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Coparticipação"
              value={form.copayment}
              onChange={(e) => setForm({ ...form, copayment: e.target.value as FormState["copayment"] })}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Não informado</MenuItem>
              <MenuItem value="yes">Sim</MenuItem>
              <MenuItem value="no">Não</MenuItem>
            </TextField>
            <TextField
              label="Vigência de"
              type="date"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Vigência até"
              type="date"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              size="small"
              fullWidth
              error={Boolean(form.startDate && form.endDate && form.endDate < form.startDate)}
              helperText={form.startDate && form.endDate && form.endDate < form.startDate
                ? "Anterior ao início"
                : "Vazio = sem data de fim"}
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
              label={form.active ? "Ativo" : "Inativo"}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} variant="outlined" disabled={saving} sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          onClick={save}
          variant="contained"
          disabled={saving}
          sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
        >
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HealthPlanModal;
